import React from "react";

import { faCheckCircle as faCheckCircleHollow } from "@fortawesome/pro-light-svg-icons";
import {
  faCheckCircle,
  faExclamationCircle,
  faPencil,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ActionButton } from "components/AgGrid/ActionButton";
import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { Row } from "components/Layout";

import {
  openEditBusinessModal,
  openMapBusinessHashRoute,
} from "lib/navigation";
import { getCombinedLotNumber } from "lib/saleLot";

export function actionsColumnRendererFactory(tableConfiguration) {
  function actionsColumnRenderer(props) {
    const { data } = props;
    if (!data.agrinousBusiness) {
      return (
        <ActionButton
          data-tour="action-map-business"
          onClick={() =>
            openMapBusinessHashRoute({
              ...data,
              tableConfiguration,
            })
          }
        >
          <FontAwesomeIcon icon={faPencil} />
          &nbsp;Map to business
        </ActionButton>
      );
    } else {
      return (
        <ActionButton
          data-tour="action-edit-business"
          onClick={() => openEditBusinessModal(data.agrinousBusiness.id)}
        >
          <FontAwesomeIcon icon={faPencil} />
          &nbsp;Edit
        </ActionButton>
      );
    }
  }
  return actionsColumnRenderer;
}

export const mappingAction = ({ data, context }) => {
  /** *
   * If the selected option
   * - has no saleyard_code, MAP
   * - Has a different saleyard_code, OVERWRITE
   * - has the same saleyard_code DISABLED
   */
  let action = null;
  // DISABLE if there is no option selected.
  if (!data.name.value) {
    action = null;
  }
  // MAP if there is no saleyard_code for the selected option.
  else if (!data.saleyard_code) {
    action = "Map";
  } else if (data.saleyard_code !== data[context.mappingColumn]) {
    action = "Overwrite";
  }

  if (action) {
    return (
      <ActionButton
        onClick={() => {
          context.update(data);
        }}
      >
        <FontAwesomeIcon icon={faPencil} />
        &nbsp;{action}
      </ActionButton>
    );
  } else {
    return null;
  }
};

export const saleLotMappingAction = ({ data, context }) => {
  let action = null;
  // DISABLE if there is no option selected.
  if (!data.agrinousLotNumber.value || !data.agrinousLot) {
    action = null;
  } else if (!data.agrinousLot.lot_number) {
    // Apply a lot number if none entered (Not sure this will ever be the case?)
    action = "Add Lot Number";
  } else if (
    getCombinedLotNumber(data.agrinousLot).toLowerCase() !==
    `${data.number}${data.lotNumberSuffix.toLowerCase()}`
  ) {
    // Otherwise, we're overwriting an old one... not the greatest thing, but could be done.
    action = "Overwrite Lot Number";
  }

  if (action) {
    return (
      <ActionButton
        onClick={() => {
          context.update(data);
        }}
      >
        <FontAwesomeIcon icon={faPencil} />
        &nbsp;{action}
      </ActionButton>
    );
  } else {
    return null;
  }
};

export const envdMappingAction = ({ data, context }) => {
  if (!data) {
    return null;
  }

  if (
    data.livestockSaleId &&
    data.speciesId &&
    context.sales[data.livestockSaleId].species_id !== data.speciesId
  ) {
    return (
      <Row fullWidth alignCenter>
        <FaIcon icon={faExclamationCircle} color="error" />
        &nbsp;Species mismatch
      </Row>
    );
  }
  const hasDeploymentChanged =
    data.deploymentId !== data.envdDocument.deploymentId;
  const hasSaleChanged =
    data.livestockSaleId !== data.envdDocument.livestockSaleId;
  const isDirty = hasDeploymentChanged || hasSaleChanged;
  const isDeploymentSet = Boolean(data.deploymentId);
  const isSaleSet = Boolean(data.livestockSaleId);

  let action = null;

  if (!isDirty) {
    if (isSaleSet && isDeploymentSet) {
      return (
        <Row fullWidth alignCenter>
          <FaIcon icon={faCheckCircle} color="success" />
          &nbsp;Mapped to Sale &amp; Agency
        </Row>
      );
    } else if (isSaleSet) {
      return (
        <Row fullWidth alignCenter>
          <FaIcon icon={faCheckCircleHollow} color="success" />
          &nbsp;Mapped to Sale
        </Row>
      );
    } else if (isDeploymentSet) {
      return (
        <Row fullWidth alignCenter>
          <FaIcon icon={faCheckCircleHollow} color="success" />
          &nbsp;Mapped to Agency
        </Row>
      );
    }
  } else if (isDeploymentSet && isSaleSet) {
    action = "Map To Agency & Sale";
  } else if (isDeploymentSet) {
    action = "Map To Agency";
  } else if (isSaleSet) {
    action = "Map To Sale";
  } else {
    action = "Clear mapping";
  }

  if (action) {
    return (
      <ActionButton
        onClick={() => {
          context.update(data);
        }}
      >
        <FontAwesomeIcon icon={faPencil} />
        &nbsp;{action}
      </ActionButton>
    );
  } else {
    return null;
  }
};
